import { supabase } from '../lib/supabase'
import { applyRosterOrder, type User } from './users'

export interface Group {
  id: string
  school_id: string
  name: string
  description: string | null
  is_entre_club: boolean
  created_by: string
  created_at: string
  updated_at: string
}

export interface GroupWithMemberCount extends Group {
  member_count: number
}

export interface GroupMember {
  id: string
  student_id: string
  student_name: string
  grade: string | null
  class: string | null
  attendance_number: string | null
}

export interface GroupWithMembers extends Group {
  members: GroupMember[]
}

export class GroupService {
  /**
   * アントレ部を実施している学校の ID。「アントレ部グループがあるか」が実施の定義で、
   * 生徒側の表示条件（アントレ部グループのメンバーか）とも揃う。
   *
   * RLS がそのまま効くので、呼び出す人によって返る範囲が変わる：先生は自校のグループ
   * しか読めないので「自校が実施校か」の判定になり、管理者は実施校の一覧になる。
   */
  static async getEntreClubSchoolIds(): Promise<Set<string>> {
    const { data, error } = await supabase
      .from('groups')
      .select('school_id')
      .eq('is_entre_club', true)

    if (error) throw error
    return new Set((data ?? []).map(g => g.school_id))
  }

  /**
   * Get all groups for a school with member counts
   */
  static async getSchoolGroups(schoolId: string): Promise<GroupWithMemberCount[]> {
    try {
      // Get all groups for the school
      const { data: groups, error: groupsError } = await supabase
        .from('groups')
        .select('*')
        .eq('school_id', schoolId)
        .order('name', { ascending: true })

      if (groupsError) {
        console.error('Error fetching groups:', groupsError)
        throw groupsError
      }

      if (!groups || groups.length === 0) {
        return []
      }

      // Get member counts for all groups
      const groupIds = groups.map(g => g.id)
      const { data: memberCounts, error: countsError } = await supabase
        .from('group_members')
        .select('group_id')
        .in('group_id', groupIds)

      if (countsError) {
        console.error('Error fetching member counts:', countsError)
      }

      // Create a map of group_id -> count
      const countMap = new Map<string, number>()
      memberCounts?.forEach(m => {
        countMap.set(m.group_id, (countMap.get(m.group_id) || 0) + 1)
      })

      // Combine groups with their member counts
      return groups.map(group => ({
        ...group,
        member_count: countMap.get(group.id) || 0
      }))
    } catch (error) {
      console.error('Error in getSchoolGroups:', error)
      throw error
    }
  }

  /**
   * Get a single group with full member details
   */
  static async getGroupWithMembers(groupId: string): Promise<GroupWithMembers | null> {
    try {
      // Get the group
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .select('*')
        .eq('id', groupId)
        .single()

      if (groupError) {
        console.error('Error fetching group:', groupError)
        throw groupError
      }

      if (!group) {
        return null
      }

      // Get members with user details
      // Use the specific foreign key relationship since there are two (student_id and added_by)
      const { data: memberships, error: membersError } = await supabase
        .from('group_members')
        .select(`
          id,
          student_id,
          users!group_members_student_id_fkey (
            id,
            name,
            grade,
            class,
            attendance_number
          )
        `)
        .eq('group_id', groupId)

      if (membersError) {
        console.error('Error fetching group members:', membersError)
        throw membersError
      }

      // Transform the data
      const members: GroupMember[] = (memberships || []).map((m: any) => ({
        id: m.id,
        student_id: m.student_id,
        student_name: m.users?.name || 'Unknown',
        grade: m.users?.grade || null,
        class: m.users?.class || null,
        attendance_number: m.users?.attendance_number || null
      }))

      return {
        ...group,
        members
      }
    } catch (error) {
      console.error('Error in getGroupWithMembers:', error)
      throw error
    }
  }

  /**
   * Create a new group
   */
  static async createGroup(data: {
    schoolId: string
    name: string
    description?: string
    isEntreClub?: boolean
  }): Promise<Group> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { data: group, error } = await supabase
        .from('groups')
        .insert({
          school_id: data.schoolId,
          name: data.name,
          description: data.description || null,
          is_entre_club: data.isEntreClub ?? false,
          created_by: user.id
        })
        .select()
        .single()

      if (error) {
        console.error('Error creating group:', error)
        throw error
      }

      return group
    } catch (error) {
      console.error('Error in createGroup:', error)
      throw error
    }
  }

  /**
   * Update a group's details
   */
  static async updateGroup(
    groupId: string,
    updates: {
      name?: string
      description?: string
      isEntreClub?: boolean
    }
  ): Promise<Group> {
    try {
      const updatePayload: Record<string, unknown> = {}
      if (updates.name !== undefined) updatePayload.name = updates.name
      if (updates.description !== undefined) updatePayload.description = updates.description
      if (updates.isEntreClub !== undefined) updatePayload.is_entre_club = updates.isEntreClub

      const { data: group, error } = await supabase
        .from('groups')
        .update(updatePayload)
        .eq('id', groupId)
        .select()
        .single()

      if (error) {
        console.error('Error updating group:', error)
        throw error
      }

      return group
    } catch (error) {
      console.error('Error in updateGroup:', error)
      throw error
    }
  }

  /**
   * Delete a group (cascades to group_members and aggregated_student_reports)
   */
  static async deleteGroup(groupId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', groupId)

      if (error) {
        console.error('Error deleting group:', error)
        throw error
      }
    } catch (error) {
      console.error('Error in deleteGroup:', error)
      throw error
    }
  }

  /**
   * Add a student to a group
   */
  static async addStudentToGroup(groupId: string, studentId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { error } = await supabase
        .from('group_members')
        .insert({
          group_id: groupId,
          student_id: studentId,
          added_by: user.id
        })

      if (error) {
        console.error('Error adding student to group:', error)
        throw error
      }
    } catch (error) {
      console.error('Error in addStudentToGroup:', error)
      throw error
    }
  }

  /**
   * Remove a student from a group
   */
  static async removeStudentFromGroup(groupId: string, studentId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId)
        .eq('student_id', studentId)

      if (error) {
        console.error('Error removing student from group:', error)
        throw error
      }
    } catch (error) {
      console.error('Error in removeStudentFromGroup:', error)
      throw error
    }
  }

  /**
   * Add multiple students to a group (batch operation)
   */
  static async addStudentsToGroup(groupId: string, studentIds: string[]): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        throw new Error('User not authenticated')
      }

      if (studentIds.length === 0) {
        return
      }

      const memberships = studentIds.map(studentId => ({
        group_id: groupId,
        student_id: studentId,
        added_by: user.id
      }))

      const { error } = await supabase
        .from('group_members')
        .insert(memberships)

      if (error) {
        console.error('Error adding students to group:', error)
        throw error
      }
    } catch (error) {
      console.error('Error in addStudentsToGroup:', error)
      throw error
    }
  }

  /**
   * Get all groups a student belongs to
   */
  static async getStudentGroups(studentId: string): Promise<Group[]> {
    try {
      const { data: memberships, error } = await supabase
        .from('group_members')
        .select(`
          groups (*)
        `)
        .eq('student_id', studentId)

      if (error) {
        console.error('Error fetching student groups:', error)
        throw error
      }

      return (memberships || []).map((m: any) => m.groups).filter(Boolean)
    } catch (error) {
      console.error('Error in getStudentGroups:', error)
      throw error
    }
  }

  /**
   * Get available students for adding to a group (students not already in the group)
   */
  static async getAvailableStudents(
    schoolId: string,
    groupId?: string
  ): Promise<User[]> {
    try {
      // Get all students in the school
      const query = applyRosterOrder(
        supabase
          .from('users')
          .select('*')
          .eq('school_id', schoolId)
          .eq('role', 'student')
      )

      const { data: students, error: studentsError } = await query

      if (studentsError) {
        console.error('Error fetching students:', studentsError)
        throw studentsError
      }

      if (!students || students.length === 0) {
        return []
      }

      // If no groupId provided, return all students
      if (!groupId) {
        return students
      }

      // Get current group members
      const { data: members, error: membersError } = await supabase
        .from('group_members')
        .select('student_id')
        .eq('group_id', groupId)

      if (membersError) {
        console.error('Error fetching group members:', membersError)
        throw membersError
      }

      // Filter out students already in the group
      const memberIds = new Set((members || []).map(m => m.student_id))
      return students.filter(s => !memberIds.has(s.id))
    } catch (error) {
      console.error('Error in getAvailableStudents:', error)
      throw error
    }
  }
}
